import React, { useContext, useLayoutEffect } from 'react';
import memoizeOne from 'memoize-one';
import { useIntl } from 'react-intl-next';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import { getATLContextDomain } from '@atlassian/atl-context';

import {
	useAnalyticsEnvironment,
	registerPendo,
	ProductIds,
} from '@confluence/choreographer-services';
// eslint-disable-next-line no-restricted-imports
import { useThemingState } from '@confluence/theming/entry-points/useThemingState';
import { Attribution } from '@confluence/error-boundary';
import {
	ExperienceStart,
	ExperienceTrackerContext,
	PENDO_INSTALLATION_EXPERIENCE,
	// eslint-disable-next-line no-restricted-imports
	getExperienceTracker,
} from '@confluence/experience-tracker';
import { useSessionData } from '@confluence/session-data';
import { LoadableBackground } from '@confluence/loadable';
import { getMonitoringClient } from '@confluence/monitoring';

import { redactUgcFromUrl } from './redactUgcFromUrl';

const APP_NAME = 'cc';

// !!!!!!!!!!!!
// !! DANGER !!
// !!!!!!!!!!!!
//
// The `@atlassian/pendo-agent-confluence` import has side-effects which boot
// the Pendo agent. We MUST load it lazily, after performing whichever tests
// determine whether we're loading it at all.
//
// See also:
//   https://atlassian.slack.com/archives/CL90JST1P/p1706833393117879?thread_ts=1706829399.015229&cid=CL90JST1P
//
const PendoComponentLoader = LoadableBackground({
	loader: async () => {
		try {
			return await import(
				/* webpackChunkName: "loadable-PendoAgentConfluence" */ '@atlassian/pendo-agent-confluence'
			);
		} catch (error) {
			getExperienceTracker().fail({
				name: PENDO_INSTALLATION_EXPERIENCE,
				error,
			});
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PERMISSIONS_EXPERIENCE,
			});
		}
	},
});
export const globalScripts = [
	{
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		script(_step: any, _guide: any) {
			const acceptedJavaScript = {
				// eslint-disable-next-line no-restricted-syntax
				'window.location.href': window.location.href,
				'window.location.pathname': window.location.pathname,
			};

			const result = processFillPolls(acceptedJavaScript);
			function getValueFromFillPoll(fillPollText: string) {
				const fillPollValue = fillPollText.split('{fillPoll: "')[1].split('"/}')[0];
				return fillPollValue;
			}
			function processFillPolls(acceptedJavaScriptObject: {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				[x: string]: any;
				'window.location.href'?: string;
				'window.location.pathname'?: string;
			}) {
				const fillPollElements = window.pendo?.dom(
					'._pendo-open-text-poll-question p[id*="pendo-text"]:contains("{fillPoll")',
				);
				if (fillPollElements.length > 0) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					fillPollElements.each((el: any) => {
						const javascriptRule = getValueFromFillPoll(el.innerText);
						const pollId = el.parentElement.parentElement.dataset.pendoPollId;
						const textarea = document.querySelector(`textarea[data-pendo-poll-id="${pollId}"]`);
						if (textarea) {
							if (textarea instanceof HTMLTextAreaElement) {
								// Now you can safely use textarea as an HTMLTextAreaElement
								textarea.value = `URL=${acceptedJavaScriptObject[javascriptRule]}`;
							}
						}
					});
				}

				return fillPollElements;
			}
			return result;
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		test(step: any, _guide: any) {
			return step;
		},
	},
	{
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		script(_step: any, _guide: any) {
			const result = processHiddenRows();

			function processHiddenRows() {
				const hiddenRows = window.pendo?.dom('div[id*="pendo-text"]:contains("{hidden/}")');
				if (hiddenRows) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					hiddenRows.each((el: any) => {
						const elUpdated = el;
						elUpdated.style.visibility = 'hidden';
						elUpdated.style.display = 'none';
						if (elUpdated.dataset.pendoPollId) {
							const linkedPollElements = window.pendo?.dom(
								`[data-pendo-poll-id="${elUpdated.dataset.pendoPollId}"]`,
							);
							if (linkedPollElements) {
								linkedPollElements.each(
									(linkedPollEl: {
										nodeName: string;
										parentNode: {
											parentNode: { style: { visibility: string; display: string } };
										};
										style: { visibility: string; display: string };
									}) => {
										const updatedLinkedPollEl = linkedPollEl;
										if (updatedLinkedPollEl.nodeName === 'FORM') {
											updatedLinkedPollEl.parentNode.parentNode.style.visibility = 'hidden';
											updatedLinkedPollEl.parentNode.parentNode.style.display = 'none';
										} else {
											updatedLinkedPollEl.style.visibility = 'hidden';
											updatedLinkedPollEl.style.display = 'none';
										}
									},
								);
							}
						}
					});
				}
				return hiddenRows;
			}
			return result;
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		test(step: any, _guide: any) {
			return step;
		},
	},
	{
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		script(_step: any, _guide: any) {
			const result = processDeepLinkButton();

			function processDeepLinkButton() {
				const button = window.pendo?.dom('button[id*="pendo-button"]');
				if (button.length > 0 && button[0].innerHTML.includes('{{billing-preview-pendo}}')) {
					button[0].innerHTML = button[0].innerHTML.replace('{{billing-preview-pendo}}', '');
					button.on('click', () => {
						const CLOUD_ADMIN_URL = `https://${getATLContextDomain('admin')}`;
						let newurl = CLOUD_ADMIN_URL; // default if newurl build fails
						const accountid = window.pendo?.getSerializedMetadata()?.account?.id;
						const dashindex = accountid.indexOf('-');
						const cloudId = accountid.substring(dashindex + 1); // strip out "jsw-" from account.id to get cloudId
						if (cloudId.length > 10) {
							newurl = `${CLOUD_ADMIN_URL}/s/${cloudId}/billing-preview`;
						}
						window.pendo?.onGuideDismissed();
						window.open(newurl, '_blank');
					});
				}
				return button;
			}
			return result;
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		test(step: any, _guide: any) {
			return step;
		},
	},
];
export const PendoImporter = () => {
	const { userId, cloudId, edition } = useSessionData();
	const environment = useAnalyticsEnvironment();
	const enablePendoPlugin = FeatureGates.checkGate('post-office_enable_pendo_choreographer');
	useLayoutEffect(() => {
		if (enablePendoPlugin && typeof window.pendo?.isReady !== 'function') {
			void registerPendo(environment, {
				additionalData: { sourceProductId: ProductIds.CONFLUENCE },
			});
		}
	}, [environment, enablePendoPlugin]);

	const globalScriptData = FeatureGates.checkGate('confluence_frontend_gsac_changes_pendo')
		? globalScripts
		: [];
	const [
		{
			isInitialized: themingInitialized,
			theme: { colorMode },
		},
	] = useThemingState();
	const { locale } = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);

	// Don't re-install
	if (typeof window.pendo?.isReady === 'function') return null;

	// Don't install Pendo for anonymous users
	if (!userId) return null;

	// Don't install Pendo before theme initialization
	if (!themingInitialized) return null;

	// Point of no return: we _will_ try install Pendo

	// Make a single-caching version of the URL redactor because the Pendo agent
	// calls `setUrl` twice every second.
	const memoizedRedactUrl = memoizeOne(redactUgcFromUrl);

	const config = {
		visitor: {
			id: userId,
			language_preference: locale,
			display_mode: colorMode || 'original',
		},
		account: {
			id: `${APP_NAME}-${cloudId}`,
			edition,
			workspace_name: window.location.hostname,
		},
		location: {
			// Ignoring must-use-route-manager rule because this gets
			// executed outside the React context.
			// eslint-disable-next-line no-restricted-syntax
			setUrl: () => memoizedRedactUrl(window.location.href),
		},
		disableGuidePseudoStyles: true,
		//Building block guides use an inline style tag for various pseudo styles (e.g. hover, carets, animations).
		//These can be turned off using this flag for application's with more restrictive CSP settings but will require custom effort to reach style parity.
		excludeAllText: true,
		guides: {
			//If true, custom code blocks in building block guides will not be allowed to run. Also, classic guides will not be shown at all.
			preventCodeInjection: true,
			globalScripts: globalScriptData,
		},
	};

	return (
		<>
			<ExperienceStart name={PENDO_INSTALLATION_EXPERIENCE} />
			<PendoComponentLoader
				config={config}
				onSuccess={() => {
					experienceTracker.succeed({
						name: PENDO_INSTALLATION_EXPERIENCE,
					});
				}}
				onFailure={() => {
					experienceTracker.fail({
						name: PENDO_INSTALLATION_EXPERIENCE,
						error: new Error(`Failed to be report ready`),
					});
					// We don't know anything about why it failed to install itself, so
					// we can't meaningfully send an error to
					// getMonitoringClient().submitError here.
				}}
			/>
		</>
	);
};
